<template>
  <div class="gift-combo" :class="comboLevel">
    <div>
      <div class="gift-combo__avatar" :style="authorAvatarStyle" />
    </div>
    <div>
      <div class="gift-combo__user-name">
        {{ authorName }}
      </div>
      <div class="gift-combo__gift-name">sent {{ treasureName }}</div>
    </div>
    <div>
      <div class="gift-combo__gift-icon" :style="treasureIconStyle" />
      <div class="gift-combo__gift-number" :key="treasureNumber">
        <span>×</span>
        <span :style="treasureNumberStyle">{{ treasureNumber }}</span>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ComboQueueEle } from './composables/use-combo';
import { toRef, computed, Ref} from 'vue';

interface IGiftComboProps {
  combo?: ComboQueueEle;
}

const props = withDefaults(defineProps<IGiftComboProps>(), {});

const combo: Ref<ComboQueueEle | undefined> = toRef(props, 'combo');

const comboLevel = computed(() => {
  const value = combo?.value?.value ?? 0;
  let level = 1;
  if (value >= 10 && value <= 99) {
    level = 2;
  } else if (value >= 100 && value <= 999) {
    level = 3;
  } else if (value >= 1000) {
    level = 4;
  }
  return {
    [`level-${level}`]: true
  };
});

const authorAvatarStyle = computed(() => ({
  backgroundImage: `url(${combo?.value?.authorAvatar})`,
}));

const authorName = computed(() => combo?.value?.authorName);

const treasureName = computed(() => combo?.value?.treasureName);


const treasureIconStyle = computed(() => ({
  backgroundImage: `url(${combo?.value?.treasureIcon})`,
}));

const treasureNumber = computed(() => {
  return combo?.value?.treasureNumber;
});

const treasureNumberStyle = computed(() => {
  const number = combo?.value?.treasureNumber ?? 0;
  let fontSize = 20;
  if (number >= 10 && number <= 49) {
    fontSize = 22;
  } else if (number >= 50 && number <= 99) {
    fontSize = 24;
  } else if (number >= 100) {
    fontSize = 26;
  }
  return {
    fontSize: `${fontSize}px`,
  };
});

</script>


<style lang="less" scoped>
.linear-background(@color1st, @color2nd) {
  & > div:nth-child(1) {
    background: @color1st;
  }
  & > div:nth-child(2) {
    background: linear-gradient(90deg, @color1st, @color2nd);
  }
  & > div:nth-child(3) {
    background: linear-gradient(90deg, @color2nd, fadeout(@color2nd, 100%));
  }
}

.gift-combo {
  height: 40px;
  border-radius: 20px 0 0 20px;
  color: #fff;
  display: inline-flex;
  max-width: 265px;
  transition: all 0.2s ease;
  margin-bottom: 18px;

  & > div:nth-child(1) {
    border-radius: 20px 0 0 20px;
    padding: 2px 0 2px 2px;
    flex: 0;
    margin-right: -1px;
  }
  & > div:nth-child(2) {
    flex: 1;
    padding: 3px 4px;
    position: relative;
    display: flex;
    flex-direction: column;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    margin-right: -1px;
  }
  & > div:nth-child(3) {
    display: flex;
    flex-direction: row;
    flex: 0;
    padding-right: 10px;
  }

  @opacity: 0;

  &.level-1 {
    .linear-background(#4c93ff, fadeout(#2A6DFF, @opacity));
  }
  &.level-2 {
    .linear-background(#9E71FF , fadeout(#793AFF, @opacity));
  }
  &.level-3 {
    .linear-background(#FF82AB, fadeout(#FF4885, @opacity));
  }
  &.level-4 {
    .linear-background(#FABB0C, fadeout(#DEA301, @opacity));
  }
  
  &__avatar {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background-size: 36px;
    background-position: center;
    background-repeat: no-repeat;
    background-image: url('@/assets/noface.png');
  }

  &__user-name {
    font-size: 14px;
    line-height: 18px;
    text-shadow: 0 1px 1px rgba(6, 6, 6, 0.3);
    font-weight: bold;
    flex: 1 1 auto;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  }

  &__gift-name {
    font-size: 12px;
    line-height: 16px;
    text-shadow: 0 1px 1px rgba(6, 6, 6, 0.3);
    flex: 1 1 auto;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  }

  &__gift-icon {
    width: 48px;
    height: 48px;
    margin-top: -4px;
    background-position: center;
    background-size: 48px;
    background-repeat: no-repeat;
    margin-right: 4px;
  }

  &__gift-number {
    font-size: 14px;
    color: #F7D006;
    align-self: center;
    display: flex;
    flex-direction: row;
    align-items: flex-end;
    text-shadow: 0 0 2px #956006;
    font-family: Roboto, Arial, Helvetica, Verdana, PT Sans Caption, sans-serif;
    transform-origin: bottom left;
    animation: twinkle 0.25s 1;
    
    
    span {
      display: inline-block;
      font-weight: bold;
      font-weight: 900;
      transform: skewX(-15deg);
      transform-origin: bottom;
    }

    span + span {
      margin-left: 4px;
      margin-bottom: -1px;
    }
  }

  @keyframes twinkle{
    0% {
      transform: scale(0);
    }
    50% {
      transform: scale(1.2);
    }
    100% {
      transform: scale(1);
    }
  }

  @-webkit-keyframes twinkle {
    0% {
      transform: scale(0);
    }
    50% {
      transform: scale(1.5);
    }
    100% {
      transform: scale(1);
    }
  }
}
</style>